import { useState } from "react";
import { ChevronRight, ChevronDown, Trash2, Save, Plus, X, Layers } from "lucide-react";

function EditRow({ comp, onSave, onCancel }) {
  const [form, setForm] = useState({ ...comp });
  const autoWt = form.blank_size_l && form.blank_size_w && form.blank_size_t
    ? (parseFloat(form.blank_size_l) * parseFloat(form.blank_size_w) * parseFloat(form.blank_size_t) * 0.00000786).toFixed(4)
    : null;

  return (
    <div className="col-span-9 grid grid-cols-[40px_1fr_50px_50px_50px_50px_50px_50px_50px_30px_60px] gap-1 items-center">
      <input className="h-7 text-xs border rounded px-1" placeholder="Sr" value={form.sr_label || ""} onChange={e => setForm({ ...form, sr_label: e.target.value })} />
      <input className="h-7 text-xs border rounded px-1" placeholder="Description" value={form.description || ""} onChange={e => setForm({ ...form, description: e.target.value })} />
      <input className="h-7 text-xs border rounded px-1" placeholder="L" type="number" value={form.blank_size_l || ""} onChange={e => setForm({ ...form, blank_size_l: e.target.value })} />
      <input className="h-7 text-xs border rounded px-1" placeholder="W" type="number" value={form.blank_size_w || ""} onChange={e => setForm({ ...form, blank_size_w: e.target.value })} />
      <input className="h-7 text-xs border rounded px-1" placeholder="T" type="number" value={form.blank_size_t || ""} onChange={e => setForm({ ...form, blank_size_t: e.target.value })} />
      <input className="h-7 text-xs border rounded px-1" placeholder="Mat" value={form.material || ""} onChange={e => setForm({ ...form, material: e.target.value })} />
      <input className="h-7 text-xs border rounded px-1" placeholder="Qty" type="number" value={form.qty_per_unit || ""} onChange={e => setForm({ ...form, qty_per_unit: e.target.value })} />
      <div className="text-xs text-slate-500 text-center">{autoWt ? `${autoWt}` : "-"}</div>
      <input className="h-7 text-xs border rounded px-1" placeholder="Rmk" value={form.remarks || ""} onChange={e => setForm({ ...form, remarks: e.target.value })} />
      <label className="text-[10px] flex items-center"><input type="checkbox" checked={form.is_assembly || false} onChange={e => setForm({ ...form, is_assembly: e.target.checked })} className="w-3 h-3" /></label>
      <div className="flex gap-0.5">
        <button className="h-6 px-1.5 text-emerald-600 hover:bg-emerald-50 rounded text-xs" onClick={() => onSave(form)}><Save className="w-3 h-3" /></button>
        <button className="h-6 px-1.5 text-slate-400 hover:bg-slate-100 rounded text-xs" onClick={onCancel}><X className="w-3 h-3" /></button>
      </div>
    </div>
  );
}

// Flatten tree for iterative rendering
function flattenTree(nodes, depth = 0, collapsed = {}) {
  const result = [];
  for (const node of nodes) {
    const hasChildren = node.children && node.children.length > 0;
    result.push({ ...node, _depth: depth, _hasChildren: hasChildren });
    if (hasChildren && !collapsed[node.id]) {
      result.push(...flattenTree(node.children, depth + 1, collapsed));
    }
  }
  return result;
}

export function ComponentTree({ tree, onUpdate, onDelete, onAddChild, canManage }) {
  const [collapsed, setCollapsed] = useState({});
  const [editingId, setEditingId] = useState(null);

  const toggleCollapse = (id) => setCollapsed(prev => ({ ...prev, [id]: !prev[id] }));
  const flat = flattenTree(tree, 0, collapsed);

  return (
    <div>
      {flat.map(comp => {
        const indent = comp._depth * 24;
        return (
          <div
            key={comp.id}
            className={`group grid grid-cols-[20px_20px_40px_1fr_110px_50px_50px_60px_80px] items-center gap-1 py-1.5 px-4 border-b border-slate-100 hover:bg-slate-50 transition-colors ${editingId === comp.id ? "bg-blue-50/50" : ""}`}
            data-testid={`component-row-${comp.id}`}
          >
            {/* Col 1: Toggle */}
            <button
              className="w-5 h-5 flex items-center justify-center"
              style={{ marginLeft: `${indent}px` }}
              onClick={() => comp._hasChildren && toggleCollapse(comp.id)}
            >
              {comp._hasChildren ? (collapsed[comp.id] ? <ChevronRight className="w-3.5 h-3.5 text-slate-500" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-500" />) : null}
            </button>

            {/* Col 2: Assembly icon */}
            <div className="flex items-center justify-center">
              {comp.is_assembly && <Layers className="w-3.5 h-3.5 text-amber-500" />}
            </div>

            {editingId === comp.id ? (
              <EditRow comp={comp} onSave={(data) => { onUpdate(comp.id, data); setEditingId(null); }} onCancel={() => setEditingId(null)} />
            ) : (
              <>
                {/* Col 3: Sr Label */}
                <span className="text-xs text-slate-400 font-mono truncate">{comp.sr_label}</span>

                {/* Col 4: Description */}
                <span
                  className={`text-sm truncate ${canManage ? "cursor-pointer" : ""} ${comp.is_assembly ? "font-semibold text-slate-800" : "text-slate-700"}`}
                  style={{ paddingLeft: `${indent}px` }}
                  onClick={canManage ? () => setEditingId(comp.id) : undefined}
                >
                  {comp.description}
                </span>

                {/* Col 5: Dimensions */}
                <span className="text-xs text-slate-400 text-right truncate">
                  {comp.blank_size_l ? `${comp.blank_size_l}x${comp.blank_size_w}x${comp.blank_size_t}` : ""}
                </span>

                {/* Col 6: Material */}
                <span className="text-[10px] text-slate-500 truncate">{comp.material || ""}</span>

                {/* Col 7: Qty */}
                <span className="text-xs text-slate-500 text-right">x{comp.qty_per_unit || 0}</span>

                {/* Col 8: Weight */}
                <span className="text-xs text-slate-400 text-right">
                  {comp.weight_per_piece > 0 ? `${parseFloat(comp.weight_per_piece).toFixed(2)}kg` : ""}
                </span>

                {/* Col 9: Actions */}
                {canManage ? (
                  <div className="opacity-0 group-hover:opacity-100 flex gap-0.5 justify-end">
                    <button className="p-1 text-blue-500 hover:bg-blue-100 rounded" onClick={() => setEditingId(comp.id)} title="Edit"><Save className="w-3 h-3" /></button>
                    <button className="p-1 text-emerald-500 hover:bg-emerald-100 rounded" onClick={() => onAddChild(comp.id)} title="Add child"><Plus className="w-3 h-3" /></button>
                    <button className="p-1 text-red-400 hover:bg-red-100 rounded" onClick={() => onDelete(comp.id)} title="Delete"><Trash2 className="w-3 h-3" /></button>
                  </div>
                ) : <span />}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
